import {BrowserModule} from '@angular/platform-browser';
import {ErrorHandler, Injectable, LOCALE_ID, NgModule} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeRo from '@angular/common/locales/ro';
import {MAT_DATE_LOCALE} from "@angular/material/core";

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NbDatepickerModule, NbGlobalPhysicalPosition, NbThemeModule, NbToastrModule} from '@nebular/theme';
import {SharedModule} from './shared/shared.module';
import {CoreModule} from './core/core.module';
import {LayoutModule} from '@angular/cdk/layout';
import {HTTP_INTERCEPTORS, HttpErrorResponse} from '@angular/common/http';
import {JwtInterceptor} from './shared/services/jwt.interceptor';

registerLocaleData(localeRo, 'ro');

import * as Sentry from "@sentry/browser";
import {environment} from "../environments/environment";

Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.environment,
    release: environment.release,
    // TryCatch has to be configured to disable XMLHttpRequest wrapping, as we are going to handle
    // http module exceptions manually in Angular's ErrorHandler and we don't want it to capture the same error twice.
    // Please note that TryCatch configuration requires at least @sentry/browser v5.16.0.
    integrations: [new Sentry.Integrations.TryCatch({
        XMLHttpRequest: false,
    })],
});

// Expose Sentry on window in non-production builds so it can be tested from the browser console
// (e.g. `Sentry.captureException(new Error("test"))`). Not exposed in production.
if (!environment.production) {
    (window as any).Sentry = Sentry;
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
    constructor() {}

    extractError(error) {
        // Try to unwrap zone.js error.
        // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
        if (error && error.ngOriginalError) {
            error = error.ngOriginalError;
        }

        // We can handle messages and Error objects directly.
        if (typeof error === "string" || error instanceof Error) {
            return error;
        }

        // If it's http module error, extract as much information from it as we can.
        if (error instanceof HttpErrorResponse) {
            // The `error` property of http exception can be either an `Error` object, which we can use directly...
            if (error.error instanceof Error) {
                return error.error;
            }

            // ... or an`ErrorEvent`, which can provide us with the message but no stack...
            if (error.error instanceof ErrorEvent) {
                return error.error.message;
            }

            // ...or the request body itself, which we can use as a message instead.
            if (typeof error.error === "string") {
                return `Server returned code ${error.status} with body "${error.error}"`;
            }

            // If we don't have any detailed information, fallback to the request message itself.
            return error.message;
        }

        // Skip if there's no error, and let user decide what to do with it.
        return null;
    }

    handleError(error) {
        // HTTP errors are already captured with full context in JwtInterceptor.
        // Skip them here so the same event is not sent to Sentry twice.
        const unwrapped = (error && error.ngOriginalError) || error;
        if (unwrapped instanceof HttpErrorResponse) {
            if (!environment.production) {
                console.error(unwrapped);
            }
            return;
        }

        const extractedError = this.extractError(error) || "Handled unknown error";

        // Capture handled exception and send it to Sentry.
        Sentry.captureException(extractedError);

        // When in development mode, log the error to console for immediate feedback.
        if (!environment.production) {
            console.error(extractedError);
        }
    }
}


@NgModule({
    declarations: [
        AppComponent
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        NbThemeModule.forRoot({name: 'default'}),
        SharedModule,
        CoreModule,
        LayoutModule,
        NbToastrModule.forRoot({
            duration: 4000,
            position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
            destroyByClick: true,
            icon: ''
        }),
        NbDatepickerModule.forRoot(),
        // SocialLoginModule
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtInterceptor,
            multi: true
        },
        {
            provide: LOCALE_ID,
            useValue: "ro"
        },
        {
            provide: MAT_DATE_LOCALE,
            useExisting: LOCALE_ID
        },
        {
            provide: ErrorHandler,
            useClass: SentryErrorHandler
        }
        // {
        //     provide: AuthServiceConfig,
        //     useFactory: provideSocialConfig
        // }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
